import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchSources, deleteSource } from '../api';
import type { SourceInfo } from '../types';
import { formatCurrency } from '../utils/format';

interface Props {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}

export function SourcesPanel({ open, onClose, onChanged }: Props) {
  const [sources, setSources] = useState<SourceInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchSources();
      if (res.ok) setSources(res.sources);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const handleDelete = async (name: string) => {
    setDeleting(name);
    try {
      await deleteSource(name);
      setConfirmDelete(null);
      void load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/60 p-0 sm:items-center sm:p-4">
      <div className="w-full max-w-lg rounded-2xl border border-zinc-700 bg-zinc-900 p-5 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-100">
            📄 Uploaded Documents
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:text-slate-300"
          >
            Close
          </button>
        </div>

        {error && (
          <p className="mb-3 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
            {error}
          </p>
        )}

        {loading ? (
          <div className="flex h-32 items-center justify-center text-sm text-zinc-500">
            Loading...
          </div>
        ) : sources.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-zinc-600">
            No documents uploaded yet.
          </div>
        ) : (
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {sources.map((s) => (
              <div
                key={s.name}
                className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-800/40 px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-200">
                    {s.name}
                  </p>
                  <div className="mt-1 flex items-center gap-3 text-xs text-slate-400">
                    <span>{s.transactionCount} txns</span>
                    <span className="text-rose-400">
                      {formatCurrency(s.totalSpent)} spent
                    </span>
                    {s.totalIncome > 0 && (
                      <span className="text-emerald-400">
                        {formatCurrency(s.totalIncome)} income
                      </span>
                    )}
                    <span className="text-zinc-600">
                      {s.lastDate}
                    </span>
                  </div>
                </div>

                <div className="ml-3 shrink-0">
                  {confirmDelete === s.name ? (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-amber-400">Delete all?</span>
                      <button
                        type="button"
                        onClick={() => void handleDelete(s.name)}
                        disabled={deleting === s.name}
                        className="rounded-lg bg-rose-600 px-2 py-1 text-xs font-semibold text-white transition hover:bg-rose-500 disabled:opacity-50"
                      >
                        {deleting === s.name ? '...' : 'Yes'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(null)}
                        className="rounded-lg px-2 py-1 text-xs font-semibold text-slate-400 hover:text-slate-200"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(s.name)}
                      className="rounded-lg border border-zinc-700 bg-zinc-800 px-2.5 py-1 text-xs font-semibold text-zinc-400 transition hover:border-rose-500/50 hover:text-rose-400"
                      title="Delete all transactions from this document"
                    >
                      🗑 Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
